import apiClient from './client'
import type {
  Script,
  CreateScriptDto,
  UpdateScriptDto,
  CompileScriptResponse,
  ExportPineScriptOptions,
  ExportPineScriptResponse,
} from '@/types/script'
import type { DatasetMetadata } from '@/types/metadata'

export const scriptsApi = {
  list: async (options?: {
    limit?: number;
    offset?: number;
    isTemplate?: boolean;
    isPublic?: boolean;
    search?: string;
    hasDataset?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    scripts: Script[]
    pagination: {
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }
  }> => {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.isTemplate !== undefined) params.append('isTemplate', options.isTemplate.toString())
    if (options?.isPublic !== undefined) params.append('isPublic', options.isPublic.toString())
    if (options?.search) params.append('search', options.search)
    if (options?.hasDataset) params.append('hasDataset', options.hasDataset)
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom)
    if (options?.dateTo) params.append('dateTo', options.dateTo)
    if (options?.sortBy) params.append('sortBy', options.sortBy)
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder)
    
    const response = await apiClient.get(`/scripts?${params.toString()}`)
    const data = response.data
    
    // Handle both old format (array) and new format (object with scripts and pagination)
    if (Array.isArray(data)) {
      return {
        scripts: data,
        pagination: {
          total: data.length,
          limit: data.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    if (data?.scripts && Array.isArray(data.scripts)) {
      return {
        scripts: data.scripts,
        pagination: data.pagination || {
          total: data.scripts.length,
          limit: data.scripts.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    return {
      scripts: [],
      pagination: {
        total: 0,
        limit: 0,
        offset: 0,
        hasMore: false,
      },
    }
  },

  get: async (id: string): Promise<Script> => {
    const response = await apiClient.get(`/scripts/${id}`)
    // Backend returns { success: true, script: {...} } or just script
    return response.data?.script || response.data
  },

  create: async (data: CreateScriptDto): Promise<{ script: Script; compilation?: CompileScriptResponse }> => {
    const response = await apiClient.post('/scripts', data)
    // Backend returns { success: true, script: {...}, compilation: {...} }
    return {
      script: response.data?.script || response.data,
      compilation: response.data?.compilation,
    }
  },

  update: async (id: string, data: UpdateScriptDto): Promise<{ script: Script; compilation?: CompileScriptResponse }> => {
    const response = await apiClient.put(`/scripts/${id}`, data)
    // Backend returns { success: true, script: {...}, compilation: {...} }
    return {
      script: response.data?.script || response.data,
      compilation: response.data?.compilation,
    }
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scripts/${id}`)
  },

  compile: async (id: string): Promise<CompileScriptResponse> => {
    const response = await apiClient.post(`/scripts/${id}/compile`)
    return response.data
  },

  compileCode: async (dslCode: string): Promise<CompileScriptResponse> => {
    const response = await apiClient.post('/scripts/compile', { dslCode })
    return response.data
  },

  updateMetadata: async (id: string, metadata: DatasetMetadata): Promise<{ success: boolean; metadata: DatasetMetadata; metadataHash?: string }> => {
    const response = await apiClient.put(`/scripts/${id}/metadata`, { metadata })
    return response.data
  },

  exportPineScript: async (
    id: string,
    options: ExportPineScriptOptions
  ): Promise<ExportPineScriptResponse> => {
    const response = await apiClient.post(`/scripts/${id}/export/pine-script`, options)
    return response.data
  },
}
